import { useMemo } from 'react';
import type { TableProps } from 'antd/es/table';

import type { AdminHtsSubmissionResultsQuery } from '@evaltick/shared';

import { DataTable } from '@/components/DataTable';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { AdminService, type AdminHtsSubmissionListItem } from '@/services/adminService';
import { ADMIN_TABLE_PAGE_SIZE } from '@/utils/constants/admin';

import { HtsSubmissionsFilterBar } from './HtsSubmissionsFilterBar';
import { buildHtsSubmissionColumns } from './htsSubmissionColumns';
import { HTS_READINESS_BAND_OPTIONS, HTS_SUBMISSION_STATUS_OPTIONS } from './resultsFilterOptions';
import { htsSubmissionMobileRow } from './resultsMobileRows';
import { useHtsSubmissionsResultsFilters } from './useResultsListFilters';
import { useResultsTableData } from './useResultsTableData';
import { useSortOrderState } from './useSortOrderState';

// Module-level so this fetch's identity is stable across renders — an inline
// arrow passed straight into useResultsTableData would be a new function on
// every render, breaking its effect's memoization and re-fetching in a loop.
const fetchHtsSubmissionsPage = (service: AdminService, page: number, filters: AdminHtsSubmissionResultsQuery) =>
  service.listHtsSubmissions(page, ADMIN_TABLE_PAGE_SIZE, filters);

interface HtsSubmissionsResultsPanelProps {
  urlPrefix: string;
  onView: (submissionId: string) => void;
  rowSelection?: TableProps<AdminHtsSubmissionListItem>['rowSelection'];
}

/**
 * The HTS Submissions tab of the admin Results page: its own filter bar over
 * its own server-paginated, server-sorted table. Filtering/sorting happen
 * server-side — the table only ever holds one page, so doing either
 * client-side would miss every other page.
 */
export function HtsSubmissionsResultsPanel({ urlPrefix, onView, rowSelection }: HtsSubmissionsResultsPanelProps) {
  const { filters, page, apiFilters, hasActiveFilters, updateFilters, setPage, clearFilters } =
    useHtsSubmissionsResultsFilters(urlPrefix);
  const { sortOrder, handleTableChange } = useSortOrderState(setPage);

  const search = useDebouncedSearch((value) => updateFilters({ search: value }), undefined, filters.search);

  const requestFilters = useMemo(
    () => ({ ...apiFilters, sortOrder: sortOrder === 'ascend' ? ('asc' as const) : sortOrder === 'descend' ? ('desc' as const) : undefined }),
    [apiFilters, sortOrder],
  );

  const { rows, totalCount, loading } = useResultsTableData(
    fetchHtsSubmissionsPage,
    requestFilters,
    page,
    setPage,
    'HTS submissions',
  );

  const columns = useMemo(() => buildHtsSubmissionColumns(onView, sortOrder), [onView, sortOrder]);

  const handleClear = () => {
    // Drops keystrokes still waiting to apply and clears the input directly
    // — the emptied URL alone wouldn't rerun the sync effect if the search
    // param was already empty when a pending keystroke got cancelled.
    search.resetSearch();
    clearFilters();
  };

  return (
    <div>
      <HtsSubmissionsFilterBar
        searchValue={search.searchInput}
        onSearchChange={search.handleSearchChange}
        statusOptions={HTS_SUBMISSION_STATUS_OPTIONS}
        status={filters.status}
        onStatusChange={(status) => updateFilters({ status })}
        bandOptions={HTS_READINESS_BAND_OPTIONS}
        band={filters.band}
        onBandChange={(band) => updateFilters({ band })}
        from={filters.from}
        to={filters.to}
        onDateRangeChange={(from, to) => updateFilters({ from, to })}
        hasActiveFilters={hasActiveFilters || search.searchInput.trim() !== ''}
        onClear={handleClear}
      />
      <DataTable<AdminHtsSubmissionListItem>
        dataSource={rows}
        columns={columns}
        rowKey="submissionId"
        loading={loading}
        page={page}
        totalCount={totalCount}
        pageSize={ADMIN_TABLE_PAGE_SIZE}
        onPageChange={setPage}
        onChange={handleTableChange as TableProps<AdminHtsSubmissionListItem>['onChange']}
        rowSelection={rowSelection}
        mobileRow={(record) => htsSubmissionMobileRow(record, onView)}
        locale={{ emptyText: hasActiveFilters ? 'No HTS submissions match these filters' : 'No HTS submissions yet' }}
      />
    </div>
  );
}
